import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, Image, Platform, Modal, TouchableWithoutFeedback, Image as RNImage, Dimensions } from 'react-native';

import { useFocusEffect } from '@react-navigation/native';
import Animated, { useSharedValue, useAnimatedStyle, withRepeat, withTiming, withDelay, withSequence, Easing } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { router } from 'expo-router';
import { ChevronRight, Coins, Trophy, Crown, Star, CircleCheck as CheckCircle, Circle, UserPlus, Mail, Cake, Gift, Medal, Tv, List, CircleCheck as CheckCircle2, CirclePlay as PlayCircle, Share2, Bell, Phone, X, User, Award, TrendingUp } from 'lucide-react-native';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { lightenColor } from '@/utils/colorUtils';
import { useTranslation } from '@/hooks/useTranslation';
import { useGamification } from '@/hooks/useGamification';
import { supabase } from '@/lib/supabase';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { trackPageView, trackUserInteraction } from '@/src/analytics';
import { usePlayer } from '@/contexts/PlayerContext';
import { modalEvents } from '@/utils/modalEvents';
import { LinearGradient } from 'expo-linear-gradient';
import { getCurrentWeekStartDate } from '@/utils/weekStartDate';
import { palette, overlay, typography } from '@/styles/tokens';

interface GamificationEvent {
  id: string;
  event_type: string;
  event_type_category: string;
  event_position: number;
  title: string;
  description: string;
  coins_reward: number;
  is_active: boolean;
  metadata: any;
  event_categories?: {
    name: string;
    description: string;
  };
}

interface UserAchievement {
  id: string;
  achievement_type: string;
  coins_earned: number;
  created_at: string;
}

interface UserGamification {
  total_coins: number;
  consecutive_days_streak: number;
  last_visit_date: string;
}

interface WeeklyRankingEntry {
  id: string;
  smartuser_id: string;
  week_start_date: string;
  week_end_date: string;
  total_coins: number;
  rank: number;
  user_email?: string;
  display_position?: number;
}

interface HallOfFameEntry {
  id: string;
  smartuser_id: string;
  total_coins_all_time: number;
  rank_all_time: number;
  achievements_count: number;
  consecutive_days_record: number;
  user_email?: string;
  display_position?: number;
}

const { width: screenWidth } = Dimensions.get('window');
const isWebDesktop = Platform.OS === 'web' && screenWidth >= 768;

const COIN_IMAGES = [
  require('@/assets/images/coin-1/coins-1-128.webp'),
  require('@/assets/images/coin-2/coins-2-128.webp'),
  require('@/assets/images/coin-3/coins-3-128.webp'),
  require('@/assets/images/coin-4/coins-4-128.webp'),
  require('@/assets/images/coin-5/coins-5-128.webp'),
  require('@/assets/images/coin-6/coins-6-128.webp'),
  require('@/assets/images/coin-7/coins-7-128.webp'),
];

const AnimatedCoin = ({ delay, size, style }: { delay: number; size: number; style: any }) => {
  const translateY = useSharedValue(0);
  const rotate = useSharedValue(0);
  const scale = useSharedValue(1);

  useEffect(() => {
    translateY.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(-8, { duration: 1500, easing: Easing.inOut(Easing.ease) }),
          withTiming(0, { duration: 1500, easing: Easing.inOut(Easing.ease) })
        ),
        -1,
        true
      )
    );

    rotate.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(-5, { duration: 2000, easing: Easing.inOut(Easing.ease) }),
          withTiming(5, { duration: 2000, easing: Easing.inOut(Easing.ease) })
        ),
        -1,
        true
      )
    );

    scale.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(1.1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
          withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) })
        ),
        -1,
        true
      )
    );
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: translateY.value },
      { rotate: `${rotate.value}deg` },
      { scale: scale.value },
    ],
  }));

  return (
    <Animated.View style={[style, animatedStyle]}>
      <Coins size={size} color={palette.accent.gold} strokeWidth={2.5} />
    </Animated.View>
  );
};

export default function RewardsScreen() {
  const { colors: { primary: primaryColor } } = useTheme();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { userGamification: gamificationData, processEvent, refreshUserGamification } = useGamification();
  const [activeTab, setActiveTab] = useState<'rewards' | 'ranking' | 'hall'>('rewards');
  const [events, setEvents] = useState<GamificationEvent[]>([]);
  const [achievements, setAchievements] = useState<UserAchievement[]>([]);
  const [userGamificationLocal, setUserGamificationLocal] = useState<UserGamification | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupedEvents, setGroupedEvents] = useState<Record<string, { events: GamificationEvent[], position: number }>>({});
  const [weeklyRanking, setWeeklyRanking] = useState<WeeklyRankingEntry[]>([]);
  const [hallOfFame, setHallOfFame] = useState<HallOfFameEntry[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [hallOfFameLoading, setHallOfFameLoading] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { language } = useTranslation();
  const { isAvailable, isLoading: campaignLoading } = useCampaignConfig();
  const { isVisible: playerIsVisible } = usePlayer();

  // Track page view on component mount
  useEffect(() => {
    trackPageView('Rewards', '/rewards');
  }, []);

  const refreshUserCoinData = useCallback(async () => {
    if (!authState.user?.smartuserId) return;

    console.log('🎁 Rewards: Refreshing user coin data (background)');

    try {
      const { data: gamificationData, error: gamificationError } = await supabase
        .from('user_gamification')
        .select('*')
        .eq('smartuser_id', authState.user.smartuserId)
        .maybeSingle();

      if (gamificationError) {
        console.error('🎁 Error refreshing user gamification:', gamificationError);
      } else if (gamificationData) {
        console.log('🎁 Refreshed coins from DB:', gamificationData.total_coins);
        setUserGamificationLocal(gamificationData);
      }

      refreshUserGamification();
    } catch (error) {
      console.error('🎁 Error in refreshUserCoinData:', error);
    }
  }, [authState.user?.smartuserId, refreshUserGamification]);

  useFocusEffect(
    useCallback(() => {
      console.log('🎁 Rewards screen focused - refreshing coin balance');
      refreshUserCoinData();
    }, [refreshUserCoinData])
  );

  useEffect(() => {
    console.log('🎁 Rewards screen opened - loading data', {
      isConnected: !!authState.user?.smartuserId,
      smartuserId: authState.user?.smartuserId
    });
    loadGamificationData();
  }, [authState.user?.smartuserId]);

  useFocusEffect(
    useCallback(() => {
      console.log('🎁 Rewards screen focused - refreshing all data');
      loadGamificationData();
    }, [authState.user?.smartuserId, language])
  );

  const loadGamificationData = async () => {
    const isUserConnected = !!authState.user?.smartuserId;

    try {
      setLoading(true);
      console.log('🎁 Loading gamification data', {
        isConnected: isUserConnected,
        smartuserId: authState.user?.smartuserId
      });

      let hasEmail = false;
      let hasPhone = false;

      // If user is connected, load their profile
      if (isUserConnected) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('email, phone_number')
          .eq('smartuser_id', authState.user.smartuserId)
          .single();

        if (userError) {
          console.error('Error loading user profile:', userError);
        } else {
          hasEmail = !!userData?.email;
          hasPhone = !!userData?.phone_number;
          console.log('🔍 User signup method - hasEmail:', hasEmail, 'hasPhone:', hasPhone);
        }
      } else {
        console.log('🔍 User not connected - loading events without user data');
      }

      // Load gamification events
      const { data: eventsData, error: eventsError } = await supabase
        .from('gamification_events')
        .select(`
          *,
          event_categories!left(
            name,
            description,
            category_position
          ),
          gamification_event_translations!left(
            title,
            description,
            message
          )
        `)
        .eq('is_active', true)
        .eq('gamification_event_translations.language_code', language)
        .order('event_position', { ascending: true });

      if (eventsError) {
        console.error('Error loading gamification events:', eventsError);
      } else {
        // Process events with translations
        const processedEvents = (eventsData || []).map(event => {
          const translations = (event as any).gamification_event_translations?.[0];
          console.log('🔍 Event type:', event.event_type, 'Title:', translations?.title || event.title);
          return {
            ...event,
            title: translations?.title || event.title,
            description: translations?.description || event.description,
            translations: translations
          };
        });

        // Show all events (no filtering needed)
        const filteredEvents = processedEvents;

        setEvents(filteredEvents);

        // Group events by category
        const grouped = filteredEvents.reduce((acc, event) => {
          const category = event.event_categories?.name || 'General';
          const categoryPosition = event.event_categories?.category_position || 999;
          if (!acc[category]) {
            acc[category] = {
              events: [],
              position: categoryPosition
            };
          }
          acc[category].events.push(event);
          return acc;
        }, {} as Record<string, { events: GamificationEvent[], position: number }>);

        // Sort events within each category by event_position
        Object.keys(grouped).forEach(category => {
          grouped[category].events.sort((a: any, b: any) => (a.event_position || 0) - (b.event_position || 0));
        });

        setGroupedEvents(grouped);
      }

      // Load user-specific data only if user is connected
      if (isUserConnected) {
        console.log('🎁 Loading fresh user achievements for user:', authState.user.smartuserId);
        const { data: achievementsData, error: achievementsError } = await supabase
          .from('user_achievements')
          .select('*')
          .eq('smartuser_id', authState.user.smartuserId)
          .order('created_at', { ascending: false });

        if (achievementsError) {
          console.error('Error loading user achievements:', achievementsError);
        } else {
          console.log('🎁 Loaded user achievements:', achievementsData?.length || 0, 'achievements');
          setAchievements(achievementsData || []);
        }

        console.log('🎁 Loading fresh user gamification data for user:', authState.user.smartuserId);
        const { data: gamificationData, error: gamificationError } = await supabase
          .from('user_gamification')
          .select('*')
          .eq('smartuser_id', authState.user.smartuserId);

        if (gamificationError) {
          console.error('Error loading user gamification:', gamificationError);
        } else {
          console.log('🎁 Loaded user gamification data:', gamificationData?.[0] || 'No data');
          const userGamificationData = gamificationData && gamificationData.length > 0 ? gamificationData[0] : null;
          setUserGamificationLocal(userGamificationData);
        }
      } else {
        console.log('🎁 User not connected - skipping user-specific data');
        setAchievements([]);
        setUserGamificationLocal(null);
      }
    } catch (error) {
      console.error('Error loading gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyRanking = async () => {
    setRankingLoading(true);
    try {
      console.log('📊 Loading weekly ranking data...');

      const { data: latestWeekRow } = await supabase
        .from('weekly_ranking')
        .select('week_start_date')
        .order('week_start_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      const weekStartStr = latestWeekRow?.week_start_date ?? getCurrentWeekStartDate();

      const { data: rankingData, error: rankingError } = await supabase
        .from('weekly_ranking')
        .select('*')
        .eq('week_start_date', weekStartStr)
        .order('total_coins', { ascending: false })
        .order('rank', { ascending: true })
        .limit(20);

      if (rankingError) {
        console.error('Error loading weekly ranking:', rankingError);
        setWeeklyRanking([]);
      } else {
        console.log('📊 Weekly ranking data loaded:', rankingData?.length || 0, 'entries');

        if (rankingData && rankingData.length > 0) {
          // Get user pseudos for the ranking entries
          const userIds = rankingData.map(entry => entry.smartuser_id);
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('smartuser_id, pseudo')
            .in('smartuser_id', userIds);

          if (usersError) {
            console.error('Error loading user pseudos for ranking:', usersError);
          }

          // Map user pseudos to ranking entries
          const usersMap = new Map((usersData || []).map(user => [user.smartuser_id, user.pseudo]));

          // Filter out entries where user doesn't exist in users table (stale data)
          let processedRanking = rankingData
            .filter(entry => usersMap.has(entry.smartuser_id))
            .map((entry, index) => ({
              ...entry,
              user_email: usersMap.get(entry.smartuser_id) || `User ${entry.smartuser_id.slice(-4)}`,
              display_position: index + 1
            }));

          console.log('📊 Filtered weekly ranking to only include existing users:', processedRanking.length, 'valid entries from', rankingData.length, 'total');
          console.log('📊 Top 3 display positions:', processedRanking.slice(0, 3).map(e => ({ position: e.display_position, rank: e.rank, coins: e.total_coins, user: e.user_email })));

          // Check if current user is in top 10
          const currentUserId = authState.user?.smartuserId;
          const isUserInTop10 = processedRanking.some(entry => entry.smartuser_id === currentUserId);

          // If user is not in top 10, fetch their position and add them
          if (currentUserId && !isUserInTop10) {
            console.log('📊 User not in top 10, fetching user position...');
            const { data: userData, error: userError } = await supabase
              .from('weekly_ranking')
              .select('*')
              .eq('smartuser_id', currentUserId)
              .eq('week_start_date', weekStartStr)
              .maybeSingle();

            if (!userError && userData) {
              console.log('📊 User position found:', userData.rank);

              // Get user pseudo
              const { data: userPseudoData } = await supabase
                .from('users')
                .select('pseudo')
                .eq('smartuser_id', currentUserId)
                .maybeSingle();

              processedRanking.push({
                ...userData,
                user_email: userPseudoData?.pseudo || `user-${currentUserId}`,
                display_position: userData.rank
              });
            } else {
              console.log('📊 User not found in weekly ranking yet');
            }
          }

          setWeeklyRanking(processedRanking);
          console.log('📊 Processed weekly ranking:', processedRanking.length, 'entries');
        } else {
          setWeeklyRanking([]);
          console.log('📊 No weekly ranking data found');
        }
      }
    } catch (error) {
      console.error('Error loading weekly ranking:', error);
      setWeeklyRanking([]);
    } finally {
      setRankingLoading(false);
    }
  };

  const loadHallOfFame = async () => {
    setHallOfFameLoading(true);
    try {
      console.log('🏆 Loading hall of fame data...');

      // Load top 20 to account for filtered users, with fallback sorting by coins
      const { data: hallOfFameData, error: hallOfFameError } = await supabase
        .from('hall_of_fame')
        .select('*')
        .order('total_coins_all_time', { ascending: false })
        .order('rank_all_time', { ascending: true })
        .limit(20);

      if (hallOfFameError) {
        console.error('Error loading hall of fame:', hallOfFameError);
        setHallOfFame([]);
      } else {
        console.log('🏆 Hall of fame data loaded:', hallOfFameData?.length || 0, 'entries');

        if (hallOfFameData && hallOfFameData.length > 0) {
          // Get user pseudos for the hall of fame entries
          const userIds = hallOfFameData.map(entry => entry.smartuser_id);
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('smartuser_id, pseudo')
            .in('smartuser_id', userIds);

          if (usersError) {
            console.error('Error loading user pseudos for hall of fame:', usersError);
          }

          // Map user pseudos to hall of fame entries
          const usersMap = new Map((usersData || []).map(user => [user.smartuser_id, user.pseudo]));

          // Filter out entries where user doesn't exist in users table (stale data)
          let processedHallOfFame = hallOfFameData
            .filter(entry => usersMap.has(entry.smartuser_id))
            .map((entry, index) => ({
              ...entry,
              user_email: usersMap.get(entry.smartuser_id) || `User ${entry.smartuser_id.slice(-4)}`,
              display_position: index + 1
            }));

          console.log('🏆 Filtered hall of fame to only include existing users:', processedHallOfFame.length, 'valid entries from', hallOfFameData.length, 'total');

          // Check if current user is in top 10
          const currentUserId = authState.user?.smartuserId;
          const isUserInTop10 = processedHallOfFame.some(entry => entry.smartuser_id === currentUserId);

          // If user is not in top 10, fetch their position and add them
          if (currentUserId && !isUserInTop10) {
            console.log('🏆 User not in top 10, fetching user position...');
            const { data: userData, error: userError } = await supabase
              .from('hall_of_fame')
              .select('*')
              .eq('smartuser_id', currentUserId)
              .maybeSingle();

            if (!userError && userData) {
              console.log('🏆 User position found:', userData.rank_all_time);

              // Get user email
              const { data: userEmailData } = await supabase
                .from('users')
                .select('email')
                .eq('smartuser_id', currentUserId)
                .maybeSingle();

              processedHallOfFame.push({
                ...userData,
                user_email: userEmailData?.email || `User ${currentUserId.slice(-4)}`,
                display_position: userData.rank_all_time
              });
            } else {
              console.log('🏆 User not found in hall of fame, creating temporary entry from gamification data');

              // User not in hall_of_fame yet, create temporary entry from their gamification data
              const { data: userGamificationData } = await supabase
                .from('user_gamification')
                .select('*')
                .eq('smartuser_id', currentUserId)
                .maybeSingle();

              const { data: userAchievementsData } = await supabase
                .from('user_achievements')
                .select('*')
                .eq('smartuser_id', currentUserId);

              const { data: userEmailData } = await supabase
                .from('users')
                .select('email')
                .eq('smartuser_id', currentUserId)
                .maybeSingle();

              if (userGamificationData) {
                // Create a temporary hall of fame entry
                const tempEntry: HallOfFameEntry = {
                  id: `temp_${currentUserId}`,
                  smartuser_id: currentUserId,
                  total_coins_all_time: userGamificationData.total_coins || 0,
                  rank_all_time: hallOfFameData.length + 1, // Position after top 10
                  achievements_count: userAchievementsData?.length || 0,
                  consecutive_days_record: userGamificationData.consecutive_days_streak || 0,
                  user_email: userEmailData?.email || `User ${currentUserId.slice(-4)}`,
                  display_position: hallOfFameData.length + 1
                };

                processedHallOfFame.push(tempEntry);
                console.log('🏆 Created temporary hall of fame entry for user:', tempEntry);
              }
            }
          }

          setHallOfFame(processedHallOfFame);
          console.log('🏆 Processed hall of fame:', processedHallOfFame.length, 'entries');
        } else {
          setHallOfFame([]);
          console.log('🏆 No hall of fame data found');
        }
      }
    } catch (error) {
      console.error('Error loading hall of fame:', error);
      setHallOfFame([]);
    } finally {
      setHallOfFameLoading(false);
    }
  };

  const hasCompletedEvent = (eventType: string): boolean => {
    return achievements.some(achievement => achievement.achievement_type === eventType);
  };

  const handleClaimReward = async (eventType: string) => {
    await processEvent(eventType);
    // Reload achievements to update UI
    await loadGamificationData();
  };

  const getEventIcon = (eventType: string) => {
    //console.log('🎨 Getting icon for event type:', eventType);
    switch (eventType) {
      case 'daily_visit':
      case 'daily_login':
        return <Coins size={24} color={palette.accent.gold} />;
      case 'watch_episode':
        return <Star size={24} color={primaryColor} />;
      case 'complete_series':
      case 'series_completed':
        return <Trophy size={24} color="#00D4AA" />;
      case 'weekly_streak':
        return <Crown size={24} color="#9333EA" />;
      case 'account_creation':
      case 'create_account':
      case 'free_account_created':
      case 'user_signup':
        return <UserPlus size={24} color={palette.semantic.info} />;
      case 'email_provided':
        return <Mail size={24} color={palette.semantic.success} />;
      case 'msisdn_provided':
        return <Phone size={24} color={palette.semantic.success} />;
      case 'pseudo_provided':
        return <User size={24} color={palette.semantic.success} />;
      case 'birth_date_provided':
        return <Cake size={24} color={palette.semantic.warning} />;
      case 'anniversary':
      case 'user_anniversary':
      case 'birthday':
        return <Gift size={24} color={primaryColor} />;
      case 'top_3_weekly_viewers':
      case 'weekly_top_3':
      case 'weekly_top_3_viewers':
        return <Medal size={24} color={palette.accent.gold} />;
      case 'season_completed':
      case 'complete_season':
        return <CheckCircle2 size={24} color="#8B5CF6" />;
      case 'rubrique_completed':
      case 'category_completed':
      case 'complete_rubrique':
      case 'rubric_completed':
        return <List size={24} color="#14B8A6" />;
      case 'episodes_batch_completed':
        return <PlayCircle size={24} color={palette.semantic.warning} />;
      case 'episode_completed':
      case 'complete_episode':
        return <PlayCircle size={24} color={palette.semantic.info} />;
      case 'social_share':
      case 'share_content':
        return <Share2 size={24} color={primaryColor} />;
      case 'follow_social':
        return <Share2 size={24} color={palette.semantic.info} />;
      case 'enable_notifications':
        return <Bell size={24} color={palette.semantic.success} />;
      case 'complete_profile':
        return <CheckCircle size={24} color="#8B5CF6" />;
      default:
        console.warn('⚠️ No icon found for event type:', eventType);
        return <Circle size={24} color={palette.neutral[350]} />;
    }
  };

  // Dynamic daily streak display logic
  const getDailyStreakDisplay = (gamificationData?: UserGamification | null) => {
    const currentStreak = gamificationData?.consecutive_days_streak || 0;
    const streakInWeek = ((currentStreak - 1) % 7) + 1;

    const days = [];
    for (let i = 1; i <= 7; i++) {
      const isCompleted = i <= streakInWeek;
      const isToday = i === streakInWeek + 1;
      days.push({ day: i, isCompleted, isToday, coins: i });
    }

    return { days };
  };

  const handleScreenTouch = () => {
    const isUserConnected = !!authState.user?.smartuserId;
    if (!isUserConnected) {
      console.log('🔒 User not connected, showing sign-in modal');
      setShowSignInModal(true);
    }
  };

  const renderRewardsTab = () => {
    const isUserConnected = !!authState.user?.smartuserId;
    const currentGamificationData = userGamificationLocal || gamificationData;
    const { days } = getDailyStreakDisplay(currentGamificationData);
    const currentStreak = currentGamificationData?.consecutive_days_streak || 0;

    const totalEvents = events.length;
    const completedEvents = events.filter(event => hasCompletedEvent(event.event_type)).length;
    const progressPercentage = totalEvents > 0 ? Math.round((completedEvents / totalEvents) * 100) : 0;

    const content = (
      <ScrollView
        style={styles.tabContent}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={Platform.OS === 'android'}
        overScrollMode="never"
        nestedScrollEnabled={true}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <Text style={styles.heroTitle}>
            {t('getCoins') || 'Get Coins,'}{'\n'}{t('keepWatching') || 'Keep Watching'}
          </Text>
        </View>

        {/* Total Coins Button */}
        {isUserConnected && (
          <View style={styles.totalCoinsWrapper}>
            <TouchableOpacity style={styles.totalCoinsButton} activeOpacity={0.8}>
              <LinearGradient
                colors={[primaryColor, lightenColor(primaryColor, 0.2)]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.totalCoinsGradient}
              >
                <View style={styles.totalCoinsLeft}>
                  <Text style={styles.totalCoinsLabel}>{t('totalCoins') || 'Total Coins'}</Text>
                  <ChevronRight size={18} color={palette.white} />
                </View>
                <View style={styles.totalCoinsRight}>
                  <Image
                    source={require('@/assets/images/coin-1/coins-1-128.webp')}
                    style={styles.totalCoinIcon}
                    resizeMode="contain"
                  />
                  <Text style={styles.totalCoinsValue}>{currentGamificationData?.total_coins || 0}</Text>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        )}

        {/* Overall Progress */}
        {isUserConnected && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('overallProgress') || 'Overall Progress'}</Text>
            <View style={styles.progressCard}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressPercentage}>
                  {progressPercentage}%
                  <Text style={styles.progressLabel}> {t('ofMissionsCompleted') || 'of missions completed'}</Text>
                </Text>
                <Text style={styles.progressCount}>{completedEvents} / {totalEvents}</Text>
              </View>
              <View style={styles.progressBarContainer}>
                <View style={[styles.progressBarFill, { width: `${progressPercentage}%`, backgroundColor: primaryColor }]} />
              </View>
            </View>
          </View>
        )}

        {/* Daily Check-In */}
        {isUserConnected && (
          <View style={styles.section}>
            {isWebDesktop ? (
              <View style={styles.dailyDesktopContainer}>
                {/* Header row */}
                <View style={styles.dailyDesktopHeaderRow}>
                  <Text style={styles.dailyDesktopTitle}>{t('dailyCheckIn') || 'Connexion quotidienne'}</Text>
                  <View style={styles.dailyDesktopStreakBadge}>
                    <Image source={COIN_IMAGES[0]} style={{ width: 16, height: 16 }} resizeMode="contain" />
                    <Text style={styles.dailyDesktopStreakBadgeText}>{t('serie') || 'Serie'} · {currentStreak} {t('days') || 'jour(s)'}</Text>
                  </View>
                  <View style={{ flex: 1 }} />
                </View>

                {/* Body: summary card + days */}
                <View style={styles.dailyDesktopBody}>
                  {/* Left summary card */}
                  <View style={styles.dailyDesktopSummary}>
                    <Text style={styles.dailyDesktopSummaryLabel}>{t('nextReward') || 'Prochaine recompense'}</Text>
                    <View style={styles.dailyDesktopSummaryReward}>
                      <Image source={COIN_IMAGES[Math.min(days.findIndex(d => d.isToday), 6)]} style={{ width: 28, height: 28 }} resizeMode="contain" />
                      <Text style={styles.dailyDesktopSummaryCoins}>+{days.find(d => d.isToday)?.coins || days[days.length - 1].coins}</Text>
                    </View>
                    <Text style={styles.dailyDesktopSummaryAvailable}>{t('availableTomorrow') || 'Disponible demain'}</Text>
                    <View style={styles.dailyDesktopSummarySep} />
                    <View style={styles.dailyDesktopSummaryStats}>
                      <View>
                        <Text style={styles.dailyDesktopStatLabel}>{t('serie') || 'Serie'}</Text>
                        <Text style={styles.dailyDesktopStatValue}>{currentStreak} {t('day') || 'jour'}</Text>
                      </View>
                      <View>
                        <Text style={styles.dailyDesktopStatLabel}>{t('won') || 'Gagne'}</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                          <Image source={COIN_IMAGES[0]} style={{ width: 14, height: 14 }} resizeMode="contain" />
                          <Text style={styles.dailyDesktopStatValue}>{currentStreak}</Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Right: progress + day cards */}
                  <View style={styles.dailyDesktopRight}>
                    {/* Progress bar */}
                    <View style={styles.dailyDesktopProgressRow}>
                      <Text style={styles.dailyDesktopProgressLabel}>{t('weekProgress') || 'Progression de la semaine'}</Text>
                      <Text style={styles.dailyDesktopProgressCount}>{days.filter(d => d.isCompleted).length} / 7</Text>
                    </View>
                    <View style={styles.dailyDesktopProgressBar}>
                      <View style={[styles.dailyDesktopProgressFill, { width: `${(days.filter(d => d.isCompleted).length / 7) * 100}%`, backgroundColor: primaryColor }]} />
                    </View>

                    {/* Day cards in a single row */}
                    <View style={styles.dailyDesktopDaysRow}>
                      {days.map((dayData) => {
                        const isBonus = dayData.day === 7;
                        const borderColor = dayData.isCompleted
                          ? '#22C55E'
                          : dayData.isToday
                            ? 'rgba(168, 85, 247, 0.8)'
                            : isBonus
                              ? '#D4A017'
                              : 'rgba(100, 80, 140, 0.5)';
                        const bgColor = dayData.isCompleted
                          ? 'rgba(34, 197, 94, 0.12)'
                          : dayData.isToday
                            ? 'rgba(168, 85, 247, 0.25)'
                            : 'rgba(50, 40, 70, 0.5)';
                        const labelText = dayData.isToday
                          ? (t('tomorrow') || 'Demain')
                          : isBonus
                            ? 'Bonus'
                            : `J${dayData.day}`;
                        const labelColor = dayData.isCompleted
                          ? '#22C55E'
                          : dayData.isToday
                            ? '#C4B5FD'
                            : isBonus
                              ? '#D4A017'
                              : 'rgba(255,255,255,0.6)';
                        const coinsColor = isBonus ? '#D4A017' : 'rgba(255,255,255,0.9)';

                        return (
                          <View key={dayData.day} style={[styles.dailyDesktopDayCard, { borderColor, backgroundColor: bgColor }]}>
                            <Text style={[styles.dailyDesktopDayLabel, { color: labelColor }]}>{labelText}</Text>
                            <View style={styles.dailyDesktopDayCoin}>
                              {dayData.isCompleted ? (
                                <CheckCircle size={24} color="#22C55E" />
                              ) : (
                                <Image
                                  source={COIN_IMAGES[Math.min(dayData.day - 1, 6)]}
                                  style={{ width: 24, height: 24 }}
                                  resizeMode="contain"
                                />
                              )}
                            </View>
                            <Text style={[styles.dailyDesktopDayCoins, { color: coinsColor }]}>+{dayData.coins}</Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </View>
              </View>
            ) : (
              <>
                <Text style={styles.sectionTitle}>{t('dailyCheckIn') || 'Daily Check-In'}</Text>
                <View style={styles.dailyCheckInCard}>
                  <View style={styles.dailyCheckInHeader}>
                    <Text style={styles.streakLabel}>
                      {t('serie') || 'Serie'} : {currentStreak} {t('days') || 'day(s)'}
                    </Text>
                  </View>

                  <View style={styles.daysRow}>
                    {days.slice(0, 4).map((dayData) => (
                      <View
                        key={dayData.day}
                        style={[
                          styles.dayCell,
                          dayData.isCompleted && styles.dayCellCompleted,
                          dayData.isToday && styles.dayCellToday,
                        ]}
                      >
                        <Text style={[styles.dayLabel, dayData.isToday && styles.dayLabelToday]}>
                          {dayData.isToday ? (t('tomorrow') || 'Tomorrow') : `D${dayData.day}`}
                        </Text>
                        <View style={styles.dayCoinContainer}>
                          {dayData.isCompleted ? (
                            <CheckCircle size={32} color="#22C55E" />
                          ) : (
                            <Image
                              source={COIN_IMAGES[Math.min(dayData.day - 1, 6)]}
                              style={styles.dayCoinImage}
                              resizeMode="contain"
                            />
                          )}
                        </View>
                        <Text style={[styles.dayCoins, dayData.isToday && styles.dayCoinsToday]}>
                          +{dayData.coins}
                        </Text>
                      </View>
                    ))}
                  </View>

                  <View style={styles.daysRow}>
                    {days.slice(4, 7).map((dayData) => (
                      <View
                        key={dayData.day}
                        style={[
                          styles.dayCell,
                          dayData.isCompleted && styles.dayCellCompleted,
                          dayData.isToday && styles.dayCellToday,
                        ]}
                      >
                        <Text style={[styles.dayLabel, dayData.isToday && styles.dayLabelToday]}>
                          {dayData.isToday ? (t('tomorrow') || 'Tomorrow') : `D${dayData.day}`}
                        </Text>
                        <View style={styles.dayCoinContainer}>
                          {dayData.isCompleted ? (
                            <CheckCircle size={32} color="#22C55E" />
                          ) : (
                            <Image
                              source={COIN_IMAGES[Math.min(dayData.day - 1, 6)]}
                              style={styles.dayCoinImage}
                              resizeMode="contain"
                            />
                          )}
                        </View>
                        <Text style={[styles.dayCoins, dayData.isToday && styles.dayCoinsToday]}>
                          +{dayData.coins}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </>
            )}
          </View>
        )}

        {/* Earn Rewards Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('earnRewards')}</Text>
          {Object.entries(groupedEvents)
            .sort(([, a], [, b]) => a.position - b.position)
            .map(([category, categoryData]) => (
            <View key={category} style={styles.categorySection}>
              <Text style={styles.categoryTitle}>
                {(() => {
                  const keyMap: Record<string, string> = {
                    'user': 'gamificationCategoryUser',
                    'profile': 'gamificationCategoryProfile',
                    'content': 'gamificationCategoryContent',
                    'general': 'gamificationCategoryGeneral',
                    'daily mission': 'gamificationCategoryDailyMission',
                    'welcome missions': 'gamificationCategoryWelcomeMissions',
                    'watch missions': 'gamificationCategoryWatchMissions',
                    'once a year': 'gamificationCategoryOnceAYear',
                  };
                  const key = keyMap[category.toLowerCase()];
                  return key ? t(key as any) : category;
                })()}
              </Text>
              {categoryData.events.map((event) => {
                const completed = hasCompletedEvent(event.event_type);

                return (
                  <View key={event.id} style={styles.rewardItem}>
                    <View style={styles.rewardIcon}>
                      {getEventIcon(event.event_type)}
                    </View>
                    <View style={styles.rewardContent}>
                      <Text style={styles.rewardTitle}>{event.title}</Text>
                      <Text style={styles.rewardDescription}>{event.description}</Text>
                      <Text style={styles.rewardCoins}>+{event.coins_reward} Coin{event.coins_reward > 1 ? 's' : ''}</Text>
                    </View>
                    {isUserConnected && (
                      <Text style={[
                        styles.rewardStatusText,
                        completed && styles.rewardStatusTextCompleted
                      ]}>
                        {completed ? t('completed') : t('toDo')}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    );

    if (!isUserConnected) {
      return (
        <TouchableWithoutFeedback onPress={handleScreenTouch}>
          <View style={{ flex: 1 }}>
            {content}
          </View>
        </TouchableWithoutFeedback>
      );
    }

    return content;
  };

  // Load ranking data when tab becomes active
  useEffect(() => {
    if (activeTab === 'ranking' && weeklyRanking.length === 0) {
      loadWeeklyRanking();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'hall' && hallOfFame.length === 0) {
      loadHallOfFame();
    }
  }, [activeTab]);

  const renderRankingTab = () => {
    const isUserConnected = !!authState.user?.smartuserId;
    const top10 = weeklyRanking.slice(0, 10);
    const currentUserEntry = weeklyRanking.length > 10 ? weeklyRanking[10] : null;
    const isCurrentUserInTop10 = top10.some(entry => entry.smartuser_id === authState.user?.smartuserId);

    const content = (
      <ScrollView
        style={styles.tabContent}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={Platform.OS === 'android'}
        overScrollMode="never"
      >
        {rankingLoading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>{t('loading')}</Text>
          </View>
        ) : weeklyRanking.length === 0 ? (
          <View style={styles.emptyState}>
            <Trophy size={48} color={palette.neutral[450]} />
            <Text style={styles.emptyText}>{t('noRankingData')}</Text>
            <Text style={styles.emptySubtext}>{t('checkBackLaterRanking')}</Text>
          </View>
        ) : (
          <View style={styles.rankingContainer}>
            <Text style={styles.rankingHeader}>{t('thisWeeksTopPlayers')}</Text>

            {top10.map((entry, index) => {
              const isCurrentUser = entry.smartuser_id === authState.user?.smartuserId;
              const displayPosition = entry.display_position || (index + 1);

              return (
                <View key={entry.id} style={[
                  styles.rankingItem,
                  isCurrentUser && styles.currentUserRankingItem
                ]}>
                  <View style={[
                    styles.rankBadge,
                    displayPosition <= 3 && styles.topRankBadge,
                    displayPosition === 1 && styles.firstPlaceBadge,
                    displayPosition === 2 && styles.secondPlaceBadge,
                    displayPosition === 3 && styles.thirdPlaceBadge
                  ]}>
                    <Text style={[
                      styles.rankText,
                      displayPosition <= 3 && styles.topRankText
                    ]}>
                      {displayPosition}
                    </Text>
                  </View>

                  <View style={styles.userInfo}>
                    <Text style={[
                      styles.userName,
                      isCurrentUser && styles.currentUserName
                    ]}>
                      {isCurrentUser ? t('you') : entry.user_email?.split('@')[0] || `User ${entry.smartuser_id.slice(-4)}`}
                    </Text>
                    <Text style={styles.userCoins}>
                      {entry.total_coins.toLocaleString()} {entry.total_coins <= 1 ? t('coinsThisWeek').replace('pièces', 'pièce') : t('coinsThisWeek')}
                    </Text>
                  </View>

                  {displayPosition <= 3 && (
                    <View style={styles.trophyContainer}>
                      <Text style={styles.trophyEmoji}>
                        {displayPosition === 1 ? '🥇' : displayPosition === 2 ? '🥈' : '🥉'}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Show separator and current user if not in top 10 */}
            {currentUserEntry && !isCurrentUserInTop10 && (
              <>
                <View style={styles.separator}>
                  <View style={styles.separatorLine} />
                  <Text style={styles.separatorText}>•••</Text>
                  <View style={styles.separatorLine} />
                </View>

                <View style={[
                  styles.rankingItem,
                  styles.currentUserRankingItem
                ]}>
                  <View style={styles.rankBadge}>
                    <Text style={styles.rankText}>
                      {currentUserEntry.display_position || currentUserEntry.rank}
                    </Text>
                  </View>

                  <View style={styles.userInfo}>
                    <Text style={[styles.userName, styles.currentUserName]}>
                      {t('you')}
                    </Text>
                    <Text style={styles.userCoins}>
                      {currentUserEntry.total_coins.toLocaleString()} {currentUserEntry.total_coins <= 1 ? t('coinsThisWeek').replace('pièces', 'pièce') : t('coinsThisWeek')}
                    </Text>
                  </View>

                  {/* Show trophy icon only for top 3 positions */}
                  {(currentUserEntry.display_position || currentUserEntry.rank) <= 3 && (
                    <View style={styles.trophyContainer}>
                      <Text style={styles.trophyEmoji}>
                        {(currentUserEntry.display_position || currentUserEntry.rank) === 1 ? '🥇' : (currentUserEntry.display_position || currentUserEntry.rank) === 2 ? '🥈' : '🥉'}
                      </Text>
                    </View>
                  )}
                </View>
              </>
            )}
          </View>
        )}
      </ScrollView>
    );

    if (!isUserConnected) {
      return (
        <TouchableWithoutFeedback onPress={handleScreenTouch}>
          <View style={{ flex: 1 }}>
            {content}
          </View>
        </TouchableWithoutFeedback>
      );
    }

    return content;
  };

  const renderHallOfFameTab = () => {
    const isUserConnected = !!authState.user?.smartuserId;
    const top10 = hallOfFame.slice(0, 10);
    const currentUserEntry = hallOfFame.length > 10 ? hallOfFame[10] : null;
    const isCurrentUserInTop10 = top10.some(entry => entry.smartuser_id === authState.user?.smartuserId);

    const content = (
      <ScrollView
        style={styles.tabContent}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={Platform.OS === 'android'}
        overScrollMode="never"
      >
        {hallOfFameLoading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>{t('loading')}</Text>
          </View>
        ) : hallOfFame.length === 0 ? (
          <View style={styles.emptyState}>
            <Crown size={48} color={palette.neutral[450]} />
            <Text style={styles.emptyText}>{t('noHallOfFameData')}</Text>
            <Text style={styles.emptySubtext}>{t('checkBackLaterHallOfFame')}</Text>
          </View>
        ) : (
          <View style={styles.hallOfFameContainer}>
            <Text style={styles.hallOfFameHeader}>{t('allTimeChampions')}</Text>

            {top10.map((entry, index) => {
              const isCurrentUser = entry.smartuser_id === authState.user?.smartuserId;
              const displayPosition = entry.display_position || (index + 1);

              return (
                <View key={entry.id} style={[
                  styles.hallOfFameItem,
                  isCurrentUser && styles.currentUserHallOfFameItem
                ]}>
                  <View style={[
                    styles.rankBadge,
                    displayPosition <= 3 && styles.topRankBadge,
                    displayPosition === 1 && styles.firstPlaceBadge,
                    displayPosition === 2 && styles.secondPlaceBadge,
                    displayPosition === 3 && styles.thirdPlaceBadge
                  ]}>
                    <Text style={[
                      styles.rankText,
                      displayPosition <= 3 && styles.topRankText
                    ]}>
                      {displayPosition}
                    </Text>
                  </View>

                  <View style={styles.userInfo}>
                    <Text style={[
                      styles.userName,
                      isCurrentUser && styles.currentUserName
                    ]}>
                      {isCurrentUser ? t('you') : entry.user_email?.split('@')[0] || `User ${entry.smartuser_id.slice(-4)}`}
                    </Text>
                    <Text style={styles.userCoins}>
                      {entry.total_coins_all_time.toLocaleString()} {entry.total_coins_all_time <= 1 ? t('totalCoinsAllTime').replace('pièces', 'pièce') : t('totalCoinsAllTime')}
                    </Text>
                    <View style={styles.achievementStats}>
                      <Text style={styles.achievementText}>
                        {entry.achievements_count} {t('achievements')} • {entry.consecutive_days_record} {t('dayStreakRecord')}
                      </Text>
                    </View>
                  </View>

                  {displayPosition <= 3 && (
                    <View style={styles.trophyContainer}>
                      <Text style={styles.trophyEmoji}>
                        {displayPosition === 1 ? '👑' : displayPosition === 2 ? '🥈' : '🥉'}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Show separator and current user if not in top 10 */}
            {currentUserEntry && !isCurrentUserInTop10 && (
              <>
                <View style={styles.separator}>
                  <View style={styles.separatorLine} />
                  <Text style={styles.separatorText}>•••</Text>
                  <View style={styles.separatorLine} />
                </View>

                <View style={[
                  styles.hallOfFameItem,
                  styles.currentUserHallOfFameItem
                ]}>
                  <View style={styles.rankBadge}>
                    <Text style={styles.rankText}>
                      {currentUserEntry.display_position || currentUserEntry.rank_all_time}
                    </Text>
                  </View>

                  <View style={styles.userInfo}>
                    <Text style={[styles.userName, styles.currentUserName]}>
                      {t('you')}
                    </Text>
                    <Text style={styles.userCoins}>
                      {currentUserEntry.total_coins_all_time.toLocaleString()} {currentUserEntry.total_coins_all_time <= 1 ? t('totalCoinsAllTime').replace('pièces', 'pièce') : t('totalCoinsAllTime')}
                    </Text>
                    <View style={styles.achievementStats}>
                      <Text style={styles.achievementText}>
                        {currentUserEntry.achievements_count} {t('achievements')} • {currentUserEntry.consecutive_days_record} {t('dayStreakRecord')}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
          </View>
        )}
      </ScrollView>
    );

    if (!isUserConnected) {
      return (
        <TouchableWithoutFeedback onPress={handleScreenTouch}>
          <View style={{ flex: 1 }}>
            {content}
          </View>
        </TouchableWithoutFeedback>
      );
    }

    return content;
  };

  if (loading || campaignLoading) {
    return (
      <LinearGradient colors={['#000000', '#A855F7']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={styles.container}>
      <SafeAreaView style={styles.safeArea} edges={Platform.OS === 'web' ? ['left', 'right', 'bottom'] : undefined}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('loading')}</Text>
        </View>
      </SafeAreaView>
      </LinearGradient>
    );
  }

  if (!isAvailable) {
    return (
      <LinearGradient colors={['#000000', '#A855F7']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={styles.container}>
      <SafeAreaView style={styles.safeArea} edges={Platform.OS === 'web' ? ['left', 'right', 'bottom'] : undefined}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => { modalEvents.emitResetHomepage(); router.replace('/(tabs)'); }}>
              <RNImage
                source={require('@/assets/images/logo-hk.png')}
                style={styles.logoImage}
                resizeMode="contain"
              />
            </TouchableOpacity>
            </View>
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('appNotAvailable')}</Text>
          <Text style={styles.comingSoonSubtext}>{t('appNotAvailableSubtext')}</Text>
        </View>
      </SafeAreaView>
      </LinearGradient>
    );
  }

  if (Platform.OS === 'web' && playerIsVisible) {
    return null;
  }

  return (
    <LinearGradient
      colors={['#000000', '#A855F7']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={styles.container}
    >
    <SafeAreaView style={styles.safeArea} edges={Platform.OS === 'web' ? ['left', 'right', 'bottom'] : undefined}>
      <View style={[styles.desktopContainer, isWebDesktop && styles.desktopContainerConstrained]}>
      {Platform.OS !== 'web' && (
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => { modalEvents.emitResetHomepage(); router.replace('/(tabs)'); }}>
            <RNImage
              source={require('@/assets/images/logo-hk.png')}
              style={styles.logoImage}
              resizeMode="contain"
            />
          </TouchableOpacity>
        </View>
      </View>
      )}

      <View style={styles.tabBar}>
          <TouchableOpacity
            style={[styles.tabBarItem, activeTab === 'rewards' && styles.tabBarItemActive]}
            onPress={() => { setActiveTab('rewards'); trackUserInteraction({ trackingName: 'rewards_tab_click', elementType: 'tab', elementId: 'rewards', elementValue: 'rewards', typeOfAction: 'click' }); }}
            activeOpacity={0.7}
          >
            <Text style={[styles.tabBarText, activeTab === 'rewards' && styles.tabBarTextActive]}>
              {t('rewards') || 'Rewards'}
            </Text>
            {activeTab === 'rewards' && <View style={[styles.tabBarIndicator, { backgroundColor: primaryColor }]} />}
          </TouchableOpacity>
        </View>

      {activeTab === 'rewards' && renderRewardsTab()}
      </View>

      {/* Sign-In Modal */}
      <Modal
        visible={showSignInModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSignInModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('signInRequired')}</Text>
              <TouchableOpacity onPress={() => setShowSignInModal(false)}>
                <X size={24} color={palette.white} />
              </TouchableOpacity>
            </View>

            <View style={styles.modalContent}>
              <Text style={styles.modalMessage}>
                {t('pleaseSignInToAccessRewards') || 'Please sign in to access all features and track your rewards.'}
              </Text>
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalSecondaryButton}
                onPress={() => setShowSignInModal(false)}
              >
                <Text style={styles.modalSecondaryButtonText}>{t('cancel')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.modalPrimaryButton}
                onPress={() => {
                  setShowSignInModal(false);
                  router.push('/(tabs)/profile');
                }}
              >
                <Text style={styles.modalPrimaryButtonText}>{t('signIn')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
  },
  desktopContainer: {
    flex: 1,
    width: '100%',
  },
  desktopContainerConstrained: {
    width: '90%',
    alignSelf: 'center',
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 8,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoImage: {
    width: 120,
    height: 32,
  },
  // Tab Bar
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 16,
  },
  tabBarItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    position: 'relative',
  },
  tabBarItemActive: {},
  tabBarText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  tabBarTextActive: {
    color: palette.white,
  },
  tabBarIndicator: {
    position: 'absolute',
    bottom: 0,
    left: '15%',
    right: '15%',
    height: 3,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  tabContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  // Hero Section
  heroSection: {
    paddingTop: 20,
    paddingBottom: 10,
  },
  heroTitle: {
    color: palette.white,
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 36,
  },
  // Total Coins
  totalCoinsWrapper: {
    marginBottom: 28,
  },
  // Total Coins
  totalCoinsButton: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  totalCoinsGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 16,
  },
  totalCoinsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  totalCoinsLabel: {
    color: palette.white,
    fontSize: 17,
    fontWeight: '700',
  },
  totalCoinsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  totalCoinIcon: {
    width: 28,
    height: 28,
  },
  totalCoinsValue: {
    color: palette.white,
    fontSize: 22,
    fontWeight: 'bold',
  },
  // Sections
  section: {
    marginBottom: 28,
  },
  sectionTitle: {
    color: palette.white,
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 14,
  },
  // Progress Card
  progressCard: {
    backgroundColor: 'rgba(40, 30, 55, 0.9)',
    borderRadius: 16,
    padding: 18,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 12,
  },
  progressPercentage: {
    color: palette.white,
    fontSize: 18,
    fontWeight: 'bold',
  },
  progressLabel: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 13,
    fontWeight: '400',
  },
  progressCount: {
    color: palette.white,
    fontSize: 15,
    fontWeight: '600',
  },
  progressBarContainer: {
    height: 10,
    backgroundColor: 'rgba(60, 50, 80, 0.8)',
    borderRadius: 5,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 5,
    minWidth: 2,
  },
  // Daily Check-In
  dailyCheckInCard: {
    backgroundColor: 'rgba(40, 30, 55, 0.9)',
    borderRadius: 16,
    padding: 18,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
  },
  dailyCheckInHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  streakLabel: {
    color: palette.white,
    fontSize: 14,
    fontWeight: '500',
  },
  daysRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  dayCell: {
    flex: 1,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(50, 40, 70, 0.6)',
    borderRadius: 12,
    padding: 4,
    borderWidth: 1.5,
    borderColor: 'rgba(168, 85, 247, 0.4)',
  },
  dayCellCompleted: {
    borderColor: '#22C55E',
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
  },
  dayCellToday: {
    backgroundColor: 'rgba(168, 85, 247, 0.3)',
    borderColor: '#A855F7',
  },
  dayLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 2,
  },
  dayLabelToday: {
    color: palette.white,
    fontSize: 10,
  },
  dayCoinContainer: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  dayCoinImage: {
    width: 28,
    height: 28,
  },
  dayCoins: {
    color: palette.white,
    fontSize: 13,
    fontWeight: '700',
  },
  dayCoinsToday: {
    color: '#A855F7',
  },
  // Desktop Daily Check-In
  dailyDesktopContainer: {
    backgroundColor: 'rgba(20, 15, 35, 0.95)',
    borderRadius: 20,
    padding: 28,
    borderWidth: 1,
    borderColor: 'rgba(100, 80, 140, 0.3)',
  },
  dailyDesktopHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    gap: 16,
  },
  dailyDesktopTitle: {
    color: palette.white,
    fontSize: 22,
    fontWeight: '700',
  },
  dailyDesktopStreakBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(50, 40, 70, 0.8)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(100, 80, 140, 0.4)',
  },
  dailyDesktopStreakBadgeText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 14,
    fontWeight: '500',
  },
  dailyDesktopBody: {
    flexDirection: 'row',
    gap: 24,
  },
  dailyDesktopSummary: {
    width: 200,
    backgroundColor: 'rgba(50, 40, 70, 0.5)',
    borderRadius: 16,
    padding: 18,
    borderWidth: 1,
    borderColor: 'rgba(100, 80, 140, 0.3)',
  },
  dailyDesktopSummaryLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 12,
  },
  dailyDesktopSummaryReward: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  dailyDesktopSummaryCoins: {
    color: palette.white,
    fontSize: 32,
    fontWeight: '700',
  },
  dailyDesktopSummaryAvailable: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    marginBottom: 14,
  },
  dailyDesktopSummarySep: {
    height: 1,
    backgroundColor: 'rgba(100, 80, 140, 0.3)',
    marginBottom: 14,
  },
  dailyDesktopSummaryStats: {
    flexDirection: 'row',
    gap: 24,
  },
  dailyDesktopStatLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    marginBottom: 4,
  },
  dailyDesktopStatValue: {
    color: palette.white,
    fontSize: 14,
    fontWeight: '600',
  },
  dailyDesktopRight: {
    flex: 1,
  },
  dailyDesktopProgressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  dailyDesktopProgressLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: '500',
  },
  dailyDesktopProgressCount: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: '500',
  },
  dailyDesktopProgressBar: {
    height: 6,
    backgroundColor: 'rgba(50, 40, 70, 0.8)',
    borderRadius: 3,
    marginBottom: 20,
    overflow: 'hidden',
  },
  dailyDesktopProgressFill: {
    height: '100%',
    borderRadius: 3,
  },
  dailyDesktopDaysRow: {
    flexDirection: 'row',
    gap: 10,
  },
  dailyDesktopDayCard: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 6,
    borderRadius: 14,
    borderWidth: 2,
    gap: 8,
  },
  dailyDesktopDayLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  dailyDesktopDayCoin: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dailyDesktopDayCoins: {
    fontSize: 14,
    fontWeight: '700',
  },
  // Reward Items
  categorySection: {
    marginBottom: 24,
  },
  categoryTitle: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
    paddingLeft: 4,
  },
  rewardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(40, 30, 55, 0.9)',
    borderRadius: 14,
    padding: 16,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
  },
  rewardIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(60, 50, 80, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  rewardContent: {
    flex: 1,
  },
  rewardTitle: {
    color: palette.white,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 3,
  },
  rewardDescription: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 13,
    marginBottom: 3,
  },
  rewardCoins: {
    color: palette.accent.gold,
    fontSize: 12,
    fontWeight: '600',
  },
  rewardStatusText: {
    color: palette.white,
    fontSize: 13,
    fontWeight: '600',
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  rewardStatusTextCompleted: {
    color: '#22C55E',
  },
  // Ranking
  rankingContainer: {
    paddingVertical: 20,
  },
  rankingHeader: {
    color: palette.white,
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  rankingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(40, 30, 55, 0.9)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
  },
  currentUserRankingItem: {
    borderColor: 'rgba(168, 85, 247, 0.5)',
  },
  rankBadge: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(60, 50, 80, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  topRankBadge: {
    borderWidth: 2,
  },
  firstPlaceBadge: {
    backgroundColor: palette.accent.gold,
    borderColor: '#FFA500',
  },
  secondPlaceBadge: {
    backgroundColor: '#C0C0C0',
    borderColor: '#A0A0A0',
  },
  thirdPlaceBadge: {
    backgroundColor: '#CD7F32',
    borderColor: '#B8860B',
  },
  rankText: {
    color: palette.white,
    fontSize: 15,
    fontWeight: 'bold',
  },
  topRankText: {
    color: palette.black,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    color: palette.white,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 3,
  },
  currentUserName: {},
  userCoins: {
    color: palette.accent.gold,
    fontSize: 13,
    fontWeight: '500',
  },
  trophyContainer: {
    marginLeft: 12,
  },
  trophyEmoji: {
    fontSize: 24,
  },
  hallOfFameContainer: {
    paddingVertical: 20,
  },
  hallOfFameHeader: {
    color: palette.white,
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  hallOfFameItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(40, 30, 55, 0.9)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
  },
  currentUserHallOfFameItem: {
    borderColor: 'rgba(168, 85, 247, 0.5)',
  },
  achievementStats: {
    marginTop: 3,
  },
  achievementText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 11,
  },
  separator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 14,
  },
  separatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  separatorText: {
    color: 'rgba(255, 255, 255, 0.3)',
    fontSize: 16,
    marginHorizontal: 12,
  },
  // States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: palette.white,
    fontSize: 16,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyText: {
    color: palette.white,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 16,
    textAlign: 'center',
  },
  emptySubtext: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 14,
    textAlign: 'center',
  },
  comingSoonSubtext: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 8,
  },
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: overlay.scrimStrong,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#2A1A40',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: 'rgba(168, 85, 247, 0.2)',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    color: palette.white,
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalContent: {
    marginBottom: 24,
  },
  modalMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalSecondaryButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: 'rgba(60, 50, 80, 0.8)',
    alignItems: 'center',
  },
  modalSecondaryButtonText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
  },
  modalPrimaryButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: '#A855F7',
    alignItems: 'center',
  },
  modalPrimaryButtonText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '700',
  },
});